import validator from 'validator';
import _ from 'lodash';
import {
  checkIsNum,
  sanitizeDate,
  checkIfValuesAre,
  safeAdd,
  safeMult,
  checkForValidationErrors,
  hasGrantWriterFee,
  evaluateOperator,
} from 'common/utils';
import * as math from 'mathjs';
import moment from 'moment-timezone';

export const isRequiredMsg = 'is required';

export const dateRegex = '(((?:19|20)\\d\\d)-(0?[1-9]|1[012])-([12][0-9]|3[01]|0[1-9]))';

export const reviewPanelValidationErrors = (...args) => {
  const componentsToCheck = [...args];
  return (errs) => {
    return checkForValidationErrors(errs, componentsToCheck).length === 0;
  };
};

export const otherExplnIsRequired = (data, field) => {
  return (text) => {
    let prePath = field.substring(
      field.indexOf('.') + 1,
      field.lastIndexOf('.otherExpln')
    );
    let commentField = prePath + '.boc';
    let value = _.get(data, commentField);
    if (value === 'Other') {
      return privateIsRequired(text);
    }
    return true;
  };
};

export const isRequiredWithSpecificValue = (field, expectedValue, data) => {
  return (text) => {
    const value = _.get(data, field);
    return value !== expectedValue || privateIsRequired(text);
  };
};

export const isRequiredWithoutSpecificValue = (field, expectedValue, data) => {
  return (text) => {
    const value = _.get(data, field);
    return value === expectedValue || privateIsRequired(text);
  };
};

const privateIsRequired = (value) => {
  if (value === null || value === undefined) {
    return false;
  }

  if (value === {} || value === []) {
    return false;
  }

  if (checkIsNum(value) && isFinite(value)) {
    return true;
  }

  if (_.isString(value)) {
    value = value.replace(/ /g, '');
    value = value.replace(/\t/g, '');
    value = value.replace(/\n/g, '');
  }
  if ((value && value !== '' && !_.isEmpty(value)) || value === false || value === true) {
    return true;
  }
  return false;
};

export const isRequired = () => {
  return (text) => {
    return privateIsRequired(text);
  };
};

export const mustBeTrue = () => {
  return (text) => {
    if (text && text === true) {
      return true;
    }
    return false;
  };
};

export const likeBoolean = (value) => {
  return (text) => {
    return text == value;
  };
};

//For EquipmentAndSupplies
export const isRequiredIfObjectExists = (value) => {
  return (text) => {
    return value === undefined || !!text;
  };
};

export const isChecked = (value, properties) => {
  let isValid = false;
  _.forEach(properties, (property, index) => {
    isValid = isValid || _.get(value, property) === true;
  });
  return isValid;
};

export const isCheckedMinimumOneOption = (...properties) => {
  return (text) => {
    return text !== undefined && isChecked(text, properties);
  };
};

export const isAllChecked = (value, properties) => {
  let isValid = true;
  _.forEach(properties, (property, index) => {
    isValid = isValid && _.get(value, property) === true;
  });
  return isValid;
};

export const ifTrueThenCheckedAllOptions = (args, conditionValue) => {
  return (text) => {
    if (args.expectedValue === conditionValue) {
      return text !== undefined && isAllChecked(text, args.fields);
    }
    return true;
  };
};

export const ifTrueThenCheckedMinimumOneOption = (args, conditionValue) => {
  return (text) => {
    if (args.expectedValue === conditionValue) {
      return text !== undefined && isChecked(text, args.fields);
    }
    return true;
  };
};

export const bcaValidateDocuments = (args, conditionValue) => {
  return (value) => {
    if (args.expectedValue === conditionValue) {
      return value && Array.isArray(value) && value.length >= args.noOfMinAttachments;
    }
    return true;
  };
};

export const DependenciesMatchArgs = (...values) => {
  const vals = [...values];
  return () => {
    // assumes there will be a 1-to-1 on deps to args
    const toCheck = vals.slice(0, vals.length / 2);
    const toMatch = vals.slice(vals.length / 2);
    if (toCheck.length !== toMatch.length) {
      // something is wrong with the validation, just fail it
      return false;
    }
    let matches = true;
    for (let i = 0; i < toCheck.length; i++) {
      if (toCheck[i] !== null && typeof toCheck[i] === 'object') {
        let operator = toCheck[i].operator;
        let value = toCheck[i].value;
        matches &= evaluateOperator(operator, toMatch[i], value);
      } else matches &= toCheck[i] === toMatch[i];
    }
    return !!matches;
  };
};

export const HasGrantWriterFee = (value) => {
  return () => hasGrantWriterFee(value);
};

//EquipmentAndSupplies checkboxes
export const equipSupCheck = (...value) => {
  return (equipmentAndSuppliesInventory) => {
    for (let i = 0; i < value.length; ++i) {
      if (value[i] === true || (value[i] !== false && value[i] !== undefined)) {
        return true;
      }
    }
    return false;
  };
};

export const costShareWaiver = (costShareInformation, budget) => {
  return () => {
    const {
      waiverComments,
      waiverDetermination,
      waiverDeterminationAttachment,
      waiverRequestAttachment,
    } = costShareInformation;
    let validBudget = true;
    if (waiverDetermination === 'Pending') {
      return true;
    } else {
      // check budget, check attachments
      const validAttachments =
        _.size(waiverDeterminationAttachment) > 0 && _.size(waiverRequestAttachment) > 0;

      const appShare = _.get(budget, 'share.appShare');
      const originalAppShare = _.get(budget, 'share.originalAppShare');

      if (Array.isArray(appShare)) {
        _.forEach(appShare, (appItem, ind) => {
          if (appItem > _.get(originalAppShare, ind)) {
            validBudget = false;
          }
        });
      } else {
        validBudget = appShare <= originalAppShare;
      }

      return validAttachments && validBudget && !!waiverComments;
    }
  };
};

export const applyForWaiver = (costShareInformation, budget) => {
  return () => {
    const {
      waiverComments,
      waiverDetermination,
      waiverRequestAttachment,
    } = costShareInformation;
    let validBudget = true;
    if (
      costShareInformation.waiverRequested === true &&
      costShareInformation.waivers !== undefined
    ) {
      if (
        _.isEmpty(costShareInformation.waivers) ||
        !_.some(costShareInformation.waivers, (element) => element === true)
      ) {
        return false;
      }
    }
    if (waiverDetermination === 'Pending') {
      return true;
    } else {
      // check budget, check attachments
      const validAttachments = _.size(waiverRequestAttachment) > 0;

      const appShare = _.get(budget, 'share.appShare');
      const originalAppShare = _.get(budget, 'share.originalAppShare');

      return validAttachments && validBudget && !!waiverComments;
    }
  };
};

// This is a bit of a one-off...
export const isRequiredIfGreaterThan = (...value) => {
  return (text) => {
    let sum = 0;
    let requiredSum = 0;
    for (let i = 0; i < value.length; ++i) {
      // 1st arg is the value to check
      if (isNaN(value[i])) {
        continue;
      }

      if (i === 0) {
        requiredSum = math.bignumber(value[0]).toNumber();
        // Sum up the rest
      } else {
        sum = math.add(math.bignumber(sum), math.bignumber(value[i])).toNumber();
      }
    }

    // pass validation if there is no req. sum and if the sum less than min
    if (!requiredSum || sum < requiredSum) {
      return true;
    }

    if (text === null || text === undefined) {
      return false;
    }

    if (text === {} || text === []) {
      return false;
    }

    if (checkIsNum(text) && isFinite(text)) {
      return true;
    }

    if ((text && text !== '' && !_.isEmpty(text)) || text === true) {
      return true;
    }

    return false;
  };
};

//check if over 100
export const ifOverHundred = (args) => {
  let maxValue = args;
  return (text) => {
    if (!text || text == ' ') {
      return true;
    } else if (text > args) {
      return false;
    } else if (text <= args) {
      return true;
    }
  };
};

export const lessThan = (max) => {
  return (text) => {
    return text < max;
  };
};

export const lessThanOrEqualTo = (max) => {
  return (text) => {
    if (text == undefined) {
      return true;
    }
    return text <= max;
  };
};

export const greaterThan = (max) => {
  return (text) => {
    return text > max;
  };
};

export const greaterThanOrEqualTo = (max) => {
  return (text) => {
    return text >= max;
  };
};
//This is a bit of a one-off...
export const mustBeTrueIfGreaterThan = (...value) => {
  return (text) => {
    let sum = 0;
    let requiredSum = 0;
    for (let i = 0; i < value.length; ++i) {
      // 1st arg is the value to check
      if (isNaN(value[i])) {
        continue;
      }

      if (i === 0) {
        requiredSum = math.bignumber(value[0]).toNumber();
        // Sum up the rest
      } else {
        sum = math.add(math.bignumber(sum), math.bignumber(value[i])).toNumber();
      }
    }

    // pass validation if there is no req. sum and if the sum less than min
    if (!requiredSum || sum < requiredSum) {
      return true;
    }

    return mustBeTrue()(text);
  };
};

export const mustBeTrueIfPresentAndNotZero = (value) => {
  return (text) => {
    if (
      value === null ||
      value == undefined ||
      value === 0 ||
      text === true ||
      (text !== false && text !== null && text != undefined)
    ) {
      return true;
    } else {
      return false;
    }
  };
};

export const regexMatch = (...args) => {
  let argList = [...args];
  let regex = argList[0] || '';
  return (text) => {
    //pass for empty values...
    if (text === null || text === undefined || text === '') {
      return true;
    }

    if (text != null && text != undefined) {
      if (text.match(regex)) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  };
};

export const mustMatch = (field) => {
  return (text, state) => {
    if (text === _.get(state, field)) {
      return true;
    } else {
      return false;
    }
  };
};

export const mustMatchStartsWith = (field) => {
  return (text, state) => {
    if (_.get(state, field) && _.get(state, field).startsWith(text)) {
      return true;
    } else {
      return false;
    }
  };
};

export const minLength = (...args) => {
  let argList = [...args];
  let min = argList[0];
  return (text) => {
    if (checkIfValuesAre([null, undefined, ''], text) || (text && text.length >= min)) {
      return true;
    } else {
      return false;
    }
  };
};

export const maxLength = (...args) => {
  let argList = [...args];
  let max = argList[0];
  return (text) => {
    if (checkIfValuesAre([null, undefined, ''], text) || (text && text.length <= max)) {
      return true;
    } else {
      return false;
    }
  };
};

export const betweenLengths = (...args) => {
  let argList = [...args];
  let min = argList[0] || -1;
  let max = argList[1] || -1;
  return (text) => {
    if (
      checkIfValuesAre([null, undefined, ''], text) ||
      (text && text.length <= max && text.length >= min)
    ) {
      return true;
    } else {
      return false;
    }
  };
};

export const isEmail = () => {
  return (text) => {
    return validator.isEmail(text);
  };
};

export const isPhoneNumber = (value) => {
  return value.match(/^(\+\d{1}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/i);
};

export const isExtension = (value) => {
  if (checkIfValuesAre([null, undefined, ''], value)) {
    return true;
  } else {
    return value.match(/^\d{1,10}$/i);
  }
};

export const checkPhoneNumber = (value) => {
  return (value) => {
    if (!value) {
      return false;
    } else {
      return value.match(/^(\+\d{1}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/i);
    }
  };
};

export const wasClicked = () => {
  return (value) => {
    if (value === true) {
      return true;
    } else {
      return false;
    }
  };
};

export const isParseableDate = () => {
  return (value) => {
    // Allowing empty date to pass
    if (null === value || undefined === value || '' === value) {
      return true;
    }

    if (!value.match(dateRegex)) {
      return false;
    }

    let dateToCheck = sanitizeDate(value);
    if (sanitizeDate === null || dateToCheck.substr(0, 10) !== value.substr(0, 10)) {
      return false;
    } else {
      return true;
    }
  };
};

export const optionalDateInFuture = () => {
  return (value) => {
    // Allowing empty date to pass
    if (null === value || undefined === value || '' === value) {
      return true;
    }

    const dateTime = new Date(value);

    if (!value.match(dateRegex) || dateTime.toDateString() === 'Invalid Date') {
      return false;
    }

    const now = new Date();

    return dateTime > now;
  };
};

export const occursAfter = (field) => {
  return (text, state) => {
    // Allowing empty date to pass
    if (null === text || undefined === text || text === '') {
      return true;
    }

    let rawAfterField = _.get(state, field);

    // Returning true for this - this should be picked up by another validator
    if (!text.match(dateRegex) || (rawAfterField && !rawAfterField.match(dateRegex))) {
      return true;
    }

    let afterDate = sanitizeDate(rawAfterField);
    let dateToCheck = sanitizeDate(text);

    if (dateToCheck >= afterDate) {
      return true;
    } else {
      return false;
    }
  };
};

export const hasNFIRS = (field, config) => {
  return () => {
    return (
      !config ||
      !config.excludeNFIRSTypes ||
      !checkIfValuesAre(config.excludeNFIRSTypes, field)
    );
  };
};

// metarule to allow more advanced rules (a rule that takes rules as args):
// !rule1 || (rule1 && rule2)
export const ifThenAlsoPipe = (...methods) => {
  let nextPassObj = {};
  // validator passes back the needed data to validate to this composed func
  nextPassObj.nestedRulesComposed = (args) => {
    const nestedRules = [...methods];
    const conditionRule = nestedRules[0];
    const secondRule = nestedRules[1];
    const condParams = args[0];
    let passesCondition = rulesMap[conditionRule.method](...condParams.args)(
      condParams.fieldData
    );
    if (passesCondition) {
      // if the condition rule passes, we want to run the real validation rule
      const secondRuleParams = args[1];
      return rulesMap[secondRule.method](...secondRuleParams.args)(
        secondRuleParams.fieldData
      );
    } else {
      // if the condition is false, we don't want to evaluate the other rule
      return true;
    }
  };
  return nextPassObj;
};

// FDID field should always show.
// If at least one question is answered Yes, then require both the start date and FDID fields and show the start date.
// If neither question is answered Yes, then FDID is not required and start date is not shown.
export const nfirsDateFieldRule = (...values) => {
  const questionAnswers = [...values];
  return (field) => {
    const containsYes = _.some(questionAnswers, (answer) => {
      return answer === 'Y';
    });
    if (containsYes) {
      return field && field !== '' && regexMatch('^\\d{4}-\\d{2}-\\d{2}$')(field);
    } else {
      return true;
    }
  };
};

// FDID field should always show.
// If at least one question is answered Yes, then require both the start date and FDID fields and show the start date.
// If neither question is answered Yes, then FDID is not required and start date is not shown.
export const FDIDNumberValidation = (...values) => {
  const questionAnswers = [...values];
  return (field) => {
    const containsYes = _.some(questionAnswers, (answer) => {
      return answer === 'Y';
    });
    if (containsYes) {
      return field && betweenLengths(5, 5)(field);
    } else {
      return field === undefined || field === '' || betweenLengths(5, 5)(field);
    }
  };
};

export const isRequiredWithEitherValues = (...args) => {
  let values = args;
  return (text) => {
    return privateIsRequired(text) && values.includes(text);
  };
};

export const isRequiredWithNoneOfValues = (...args) => {
  let values = [...args];
  return (text) => {
    return privateIsRequired(text) && !values.includes(text);
  };
};

export const isEqualToSumOfValues = (...args) => {
  let fields = [...args];
  let data = fields.pop();
  let fieldsToAdd = fields.map((f) => _.get(data, f));
  return (text) => {
    const sum = safeAdd(...fieldsToAdd);
    return privateIsRequired(text) && sum === parseFloat(text);
  };
};

export const ifTrueThenAssertTrue = (args, data) => {
  return (text) => {
    if (text === args.expectedValue) {
      if (
        args.fields.find((field) => _.get(data, field.dataPath) !== field.expectedValue)
      ) {
        return false;
      }
    }
    return true;
  };
};

export const lessThanValueOrPercentOf = (args, data) => {
  let value = args.value;
  let percent = args.percent;
  return (text) => {
    let maxValue = _.max([value, safeMult(0.01, percent, data)]);
    return text <= maxValue;
  };
};

export const presentFutureDateInMMDDYYYY = () => {
  return (value) => {
    if (_.isEmpty(value)) {
      return false;
    }
    if (moment(value).isValid()) {
      return moment().startOf('day') <= moment(value, 'MM-DD-YYYY');
    } else {
      return false;
    }
  };
};

export const rulesMap = {
  greaterThan,
  isRequired,
  otherExplnIsRequired,
  isRequiredIfGreaterThan,
  regexMatch,
  maxLength,
  mustBeTrue,
  minLength,
  betweenLengths,
  wasClicked,
  mustBeTrueIfGreaterThan,
  isRequiredIfObjectExists,
  DependenciesMatchArgs,
  HasGrantWriterFee,
  equipSupCheck,
  costShareWaiver,
  applyForWaiver,
  ifOverHundred,
  lessThanOrEqualTo,
  hasNFIRS,
  ifThenAlsoPipe,
  nfirsDateFieldRule,
  FDIDNumberValidation,
  isRequiredWithSpecificValue,
  isRequiredWithoutSpecificValue,
  isRequiredWithEitherValues,
  isRequiredWithNoneOfValues,
  isEqualToSumOfValues,
  ifTrueThenAssertTrue,
  optionalDateInFuture,
  lessThanValueOrPercentOf,
  reviewPanelValidationErrors,
  likeBoolean,
  presentFutureDateInMMDDYYYY,
  greaterThanOrEqualTo,
  isCheckedMinimumOneOption,
  ifTrueThenCheckedAllOptions,
  ifTrueThenCheckedMinimumOneOption,
  bcaValidateDocuments,
  isEmail,
  isPhoneNumber,
};
